import { useCallback, useEffect, useRef, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { getEnvVar } from '../../utils/getEnvVar';
import { Alert, Box, Button, IconButton, Link, Tooltip } from '@mui/material';

import {
  defaultStatus,
  findSpend,
  findStatus,
  getMapKey,
  getStatusColumnObject,
  handleDoubleClickEvent,
  prepareSpendMaps,
  processRowUpdateForStatusOnly,
  SPEND_COLUMNS,
  SUPPLIER_NAME_COLUMN_WIDTH,
  SUPPLIER_NUMBER_COLUMN_WIDTH
} from './SupplierDataGridUtils';
import {
  AE_RATING_API_ERR_MSG,
  AE_RATING_PROP_NAME,
  NON_STRATEGIC,
  RISK_RATING_API_ERR_MSG,
  RISK_RATING_PROP_NAME,
  SDIS_API_ERR_MSG,
  SDIS_PROP_NAME,
  SRO_SBM_API_ERR_MSG,
  SRO_SBM_PROP_NAME,
  SRO_SBM_USER_ID_PROP_NAME,
  STRATEGIC,
  SUPPLIER_STATUS_DONOTUSE,
  SUPPLIER_STATUS_LIMITED_USE,
  SUPPLIER_STATUS_REDUCE
} from '../../shared/AppConstants';
import { supplierDataGridSx } from './supplierDataGridStyles';
import SupplierDropDownSearch from '../SupplierDropDownSearch';
import {
  findSupplier,
  getSuppliersWithSroSbmPopulated,
  handlePromiseResult,
  populateAERatingBySupplierNumber,
  populateRiskRatingBySupplierNumber
} from '../../shared/UtilFunctions';
import { SupplierDataGridTreeData } from './SupplierDataGridTreeData';
import MultilineCellEdit from './MultilineCellEdit';
import FormHelperText from '@mui/material/FormHelperText';
import SupplierStatusPopup from './SupplierStatusPopup';
import SMModal from '../../shared/SMModal';
import {
  getAERatingColumn,
  sbmColumn
} from './prospective_supplier/PSSupplierDataGridUtils';
import { getSdiMap, getSuppliersSroSbmMap } from '../../shared/APIHelper';
import {
  SPLR_ALREADY_EXISTS_ON_NON_STRATEGIC_TAB,
  SPLR_ALREADY_EXISTS_ON_STRATEGY
} from '../../shared/Messages';

export function StrategicSupplierDataGrid(props) {
  const {
    apiRef,
    isEdit,
    isCreate = false,
    strategyInformation,
    unitGroups,
    strategicSuppliers,
    updateSupplierComments,
    updateSupplierStatus,
    dispatchSupplier,
    dispatchNonStrategicSupplier,
    commonProps,
    hasDuplicateSuppliers,
    hasDuplicateNonStrategicSuppliers,
    setNonStrategicSuppliers,
    setNonSupplier,
    nonSupplier,
    collapseExpandedSuppliers,
    setCollapseExpandedSuppliers,
    clearAllStrategicSupplierExceptions
  } = props;

  const riskRatingOnlineAppUrl = getEnvVar(
    'VITE_SUPPLIERS_RISK_RATING_ONLINE_APP_URL'
  );

  const [addSupplierErrorMessage, setAddSupplierErrorMessage] = useState('');
  const [showSupplierCommentsEditModal, setShowSupplierCommentsEditModal] =
    useState(false);
  const handleCommentsModalCancel = () =>
    setShowSupplierCommentsEditModal(false);
  const [originalSupplier, setOriginalSupplier] = useState(null);
  const [updatedSupplier, setUpdatedSupplier] = useState(null);
  const [resetStatus, setResetStatus] = useState(null);

  const splrNameColumn = {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 320
  };
  const splrNameColumnEditable = {
    ...splrNameColumn,
    renderCell: (params) => {
      return params.id !== nextRowId ? (
        params.value
      ) : (
        <div style={{ width: '100%', marginTop: '5px' }}>
          <SupplierDropDownSearch
            readOnly={false}
            showFilter={true}
            control={commonProps.control}
            name="suppliersNumber"
            showSingleOption={true}
            setStatusErrorMsg={commonProps.setStatusErrorMsg}
            handleInputChange={setSelectedSuppliers}
            addSupplierError={addSupplierErrorMessage?.length > 0}
          />
          {addSupplierErrorMessage?.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <FormHelperText
                helperText=""
                helperTextId=""
                style={{ fontSize: '14px' }}
              >
                {addSupplierErrorMessage}
              </FormHelperText>
            </div>
          )}
        </div>
      );
    }
  };

  const columns = [
    {
      field: 'number',
      headerName: 'Number',
      width: SUPPLIER_NUMBER_COLUMN_WIDTH,
      minWidth: SUPPLIER_NUMBER_COLUMN_WIDTH,
      renderCell: (params) => {
        const isBlockedSupplier = params.row.activeSupplier === 'N';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{params.value}</span>
            {isBlockedSupplier && (
              <Tooltip title="Blocked">
                <Box
                  component="span"
                  sx={(theme) => ({
                    color: theme.fuel6Tokens.JddcColorIconError,
                    marginLeft: '0.25em',
                    display: 'inline-flex'
                  })}
                >
                  <fuel-icon name="do_not_disturb_on" />
                </Box>
              </Tooltip>
            )}
          </div>
        );
      },
      flex: 0
    },
    {
      ...splrNameColumnEditable,
      width: SUPPLIER_NAME_COLUMN_WIDTH,
      minWidth: SUPPLIER_NAME_COLUMN_WIDTH,
      flex: 0
    },
    getStatusColumnObject(isEdit, STRATEGIC),
    ...SPEND_COLUMNS,
    {
      field: 'forecastSpendPercentage',
      align: 'left',
      headerName: 'Forecast %',
      flex: 1,
      minWidth: 90,
      renderCell: (params) =>
        (params.row.forecastSpendPercentage
          ? Math.round(params.row.forecastSpendPercentage)
          : '0') + '%'
    },
    {
      field: 'riskRating',
      headerName: 'Risk Rating',
      align: 'left',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => (
        <Link
          href={`${riskRatingOnlineAppUrl}${params.row.number}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {params.value}
        </Link>
      )
    },
    {
      field: 'sdis',
      headerName: 'SD Indicators',
      flex: 1,
      minWidth: 130,
      sortable: false,
      valueFormatter: (value) => (value ? value.join(', ') : value)
    },
    getAERatingColumn(),
    sbmColumn,
    {
      field: 'comments',
      headerName: 'Comments',
      align: 'left',
      flex: 1,
      minWidth: 110,
      editable: isEdit,
      resizable: true,
      renderEditCell: (params) => <MultilineCellEdit {...params} />,
      renderCell: (params) => (
        <Tooltip title={params?.value?.replaceAll(/(<([^</>]+)>)/gi, ' ')}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: 410
            }}
          >
            {isEdit && (
              <IconButton size="small">
                <fuel-icon name="edit" />
              </IconButton>
            )}
            {params.value}
          </div>
        </Tooltip>
      )
    }
  ];

  function prepareUnitRow(
    dataRows,
    splr,
    hierarchy,
    unit,
    { masterInvoiceSpendMap, masterForecastSpendMap },
    parentStatusScope,
    parentStatus
  ) {
    if (splr) {
      let mapKey = getMapKey(unit.code, splr.supplierNumber);
      let obj = {
        id: dataRows.length,
        hierarchy: [...hierarchy, unit.name + ' - ' + unit.code],
        status: findStatus(splr, unit.code, parentStatus, parentStatusScope),
        r12Spend: findSpend(masterInvoiceSpendMap, mapKey),
        r12Forecast: findSpend(masterForecastSpendMap, mapKey),
        number: splr.supplierNumber,
        statusScope: unit.code,
        parentStatusScope: parentStatusScope,
        parentStatus: parentStatus,
        activeSupplier: splr?.activeSupplier
      };

      dataRows.push(obj);
    }
  }

  function prepareUnitGroupRow(
    dataRows,
    splr,
    hierarchy,
    unitGroup,
    masterSpendObj,
    parentStatus
  ) {
    if (splr && unitGroup) {
      const { masterInvoiceSpendMap, masterForecastSpendMap } = masterSpendObj;
      const mapKey = getMapKey(unitGroup.code, splr.supplierNumber);
      const unitGroupStatus = findStatus(splr, unitGroup.code, parentStatus);
      const unitCodeStatusObj = splr?.statusMap?.get(unitGroup.code);

      // Group Object
      let obj = {
        id: dataRows.length,
        hierarchy: [...hierarchy, unitGroup.name],
        status: unitGroup?.notFullySelected
          ? ' '
          : findStatus(splr, unitGroup.code, unitGroupStatus),
        r12Spend: findSpend(masterInvoiceSpendMap, mapKey),
        r12Forecast: findSpend(masterForecastSpendMap, mapKey),
        number: splr.supplierNumber,
        statusScope: unitGroup.code,
        parentStatus: parentStatus,
        statusMapSize: unitCodeStatusObj?.statusMap?.size,
        notFullySelected: unitGroup?.notFullySelected,
        activeSupplier: splr?.activeSupplier
      };

      dataRows.push(obj);

      // Process Units
      unitGroup.units.forEach((unit) => {
        prepareUnitRow(
          dataRows,
          splr,
          obj.hierarchy,
          unit,
          masterSpendObj,
          unitGroup.code,
          unitGroupStatus
        );
      });
    }
  }

  function prepareSupplier(dataRows, splr, unitGroups, masterSpendObj) {
    if (splr && unitGroups) {
      const unitGroupsSpend = [];
      const splrStatus = findStatus(splr);

      unitGroups.forEach((unitGroup) => {
        prepareUnitGroupRow(
          unitGroupsSpend,
          splr,
          [],
          unitGroup,
          masterSpendObj,
          splrStatus
        );
      });

      // Splr Object
      let obj = {
        id: dataRows ? dataRows.length : nextRowId,
        number: splr.supplierNumber,
        name: splr.supplierName,
        status: splrStatus,
        r12Spend: splr.invoiceSpend,
        r12Forecast: splr.forecastSpend,
        forecastSpendPercentage: splr.forecastSpendPercentage,
        comments: splr?.comments?.replaceAll(/(<([^</>]+)>)/gi, ' '),
        unitGroupsSpend: unitGroupsSpend,
        statusMapSize: splr?.statusMap?.size,
        riskRating: splr.riskRating,
        sroSbmName: splr[SRO_SBM_PROP_NAME],
        sroSbmUserId: splr[SRO_SBM_USER_ID_PROP_NAME],
        sdis: splr.sdis,
        aeRating: splr.aeRating,
        activeSupplier: splr?.activeSupplier
      };

      dataRows?.push(obj);
      return obj;
    }
  }

  function prepareData(strategicSuppliers, unitGroups) {
    const dataRows = [];
    const masterSpendObj = prepareSpendMaps(strategicSuppliers);

    strategicSuppliers?.forEach((splr) =>
      prepareSupplier(dataRows, splr, unitGroups, masterSpendObj)
    );
    setRows(dataRows);
    setNextRowId(dataRows.length);
  }

  // State
  const [rows, setRows] = useState([]);
  const [nextRowId, setNextRowId] = useState();
  const [refresh, setRefresh] = useState(false);
  const [toggleRefresh, setToggleRefresh] = useState(true);
  const [addedRowId, setAddedRowId] = useState(-1);
  const [addedSplrNumber, setAddedSplrNumber] = useState(null);

  const [showClearExceptionsModal, setShowClearExceptionsModal] =
    useState(false);
  const handleModalClearExceptionsCancel = () =>
    setShowClearExceptionsModal(false);

  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState(
    new Set()
  );
  const prevUnitGroupsRef = useRef();
  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (newIds) => setDetailPanelExpandedRowIds(newIds),
    []
  );

  const [expandedMap, setExpandedMap] = useState(new Map());

  const setExpandedRowInfo = useCallback(
    (rowId, childRowId, expanded) =>
      handleMasterDetailPanelExpandedRowIdsChange(rowId, childRowId, expanded),
    [handleMasterDetailPanelExpandedRowIdsChange]
  );

  useEffect(() => {
    if (collapseExpandedSuppliers) {
      setDetailPanelExpandedRowIds(new Set());
      setExpandedMap(new Map());
      setCollapseExpandedSuppliers(false);
    }
  }, [collapseExpandedSuppliers, expandedMap]);

  function findExpandedIds(rowId) {
    let expandedIds = [];
    let rowMap = expandedMap?.get(rowId);

    if (rowMap)
      for (const item of rowMap.entries())
        if (item[1]) expandedIds.push(item[0]);

    return expandedIds;
  }

  function handleMasterDetailPanelExpandedRowIdsChange(
    rowId,
    childRowId,
    expanded
  ) {
    let newExpandedMap = expandedMap;
    let rowMap = newExpandedMap.get(rowId);

    if (!rowMap) {
      rowMap = new Map();
      newExpandedMap.set(rowId, rowMap);
    }
    rowMap.set(childRowId, expanded);
    setExpandedMap(newExpandedMap);
  }

  useEffect(() => {
    const unitGroupsChanged = prevUnitGroupsRef.current !== unitGroups;

    if (strategicSuppliers && unitGroups) {
      prepareData(strategicSuppliers, unitGroups);

      if (unitGroupsChanged) {
        setDetailPanelExpandedRowIds(new Set());
        setExpandedMap(new Map());
        setRefresh((prevVal) => !prevVal);
      } else if (toggleRefresh) setRefresh((prevVal) => !prevVal);
      else setToggleRefresh(true);
    }

    prevUnitGroupsRef.current = unitGroups;
  }, [strategicSuppliers, unitGroups]);

  const getDetailPanelContent = useCallback(
    ({ row }) => (
      <SupplierDataGridTreeData
        row={row}
        isEdit={isEdit}
        updateSupplierStatus={updateSupplierStatus}
        refresh={refresh}
        findExpandedIds={findExpandedIds}
        setExpandedRowInfo={setExpandedRowInfo}
        handleCellClick={handleCellClick}
        handleProcessRowUpdateError={handleProcessRowUpdateError}
      />
    ),
    [findExpandedIds, isEdit, refresh, setExpandedRowInfo, updateSupplierStatus]
  );

  // const getDetailPanelHeight = useCallback(
  //   () => (getEnvVar('VITE_ENV') === 'local' ? 300 : 'auto'),
  //   []
  // );
  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const handleCellClick = useCallback(
    (params, event) => handleDoubleClickEvent(params, event),
    []
  );

  const handleProcessRowUpdateError = useCallback(
    (error) => console.log(error),
    []
  );

  async function setSelectedSuppliers(splr) {
    let splrNumberToAdd = splr.value;
    let foundSplr = findSupplier(strategyInformation, splrNumberToAdd);
    let splrName = splr.label.replace(`${splrNumberToAdd} - `, '');

    let splrObj = {
      ...foundSplr,
      supplierNumber: splrNumberToAdd,
      supplierName: splrName,
      status: defaultStatus,
      comments: ''
    };

    if (hasDuplicateSuppliers(splrObj)) {
      // Check if there is a duplicate on Strategic Tab
      setAddSupplierErrorMessage(SPLR_ALREADY_EXISTS_ON_STRATEGY);
      return;
    }
    if (hasDuplicateNonStrategicSuppliers(splrObj)) {
      // Check if there is a duplicate on Non-Strategic Tab
      setAddSupplierErrorMessage(SPLR_ALREADY_EXISTS_ON_NON_STRATEGIC_TAB);
      return;
    }
    setAddSupplierErrorMessage('');

    const masterSpendObj = prepareSpendMaps(foundSplr ? [foundSplr] : []);

    let gridObj = prepareSupplier(null, splrObj, unitGroups, masterSpendObj);

    setRows((oldRows) => {
      oldRows = oldRows.filter((row) => row.number !== '---');
      const newRows = [gridObj, ...oldRows];

      // reset row ids
      return newRows.map((row, index) => {
        return { ...row, id: index };
      });
    });

    apiRef.current.startCellEditMode({ id: 0, field: 'status' });
    setAddedRowId(0);
    setAddedSplrNumber(splrNumberToAdd);
    setNextRowId((prevVal) => prevVal + 1);
    setToggleRefresh(false);

    const promises = [];

    promises.push(populateAERatingBySupplierNumber(splrNumberToAdd));
    promises.push(populateRiskRatingBySupplierNumber(splrNumberToAdd));
    promises.push(getSdiMap(splrNumberToAdd));
    promises.push(getSuppliersSroSbmMap(splrNumberToAdd));

    const [aeRating, riskRating, sdis, sroSbmMap] =
      await Promise.allSettled(promises);

    handlePromiseResult(
      aeRating,
      AE_RATING_PROP_NAME,
      AE_RATING_API_ERR_MSG,
      splrObj,
      commonProps
    );
    handlePromiseResult(
      riskRating,
      RISK_RATING_PROP_NAME,
      RISK_RATING_API_ERR_MSG,
      splrObj,
      commonProps
    );
    handlePromiseResult(
      sdis,
      SDIS_PROP_NAME,
      SDIS_API_ERR_MSG,
      splrObj,
      commonProps
    );

    if (sroSbmMap.status === 'rejected') {
      commonProps.setStatusErrorMsg(SRO_SBM_API_ERR_MSG);
    } else {
      getSuppliersWithSroSbmPopulated(sroSbmMap.value, [splrObj]);
    }

    dispatchSupplier({
      type: 'add',
      payload: splrObj
    });
  }

  /**
   * Clear All Status Exceptions
   */
  function removeAllExceptions() {
    clearAllStrategicSupplierExceptions();
    setShowClearExceptionsModal(false);
  }

  // Add Supplier Event handler
  function addSupplier() {
    let val;

    try {
      val = apiRef.current.getCellValue(nextRowId, 'number');
    } catch (e) {
      // ignore; there will be exception when empty row is not there
    }

    if (val !== defaultStatus) {
      // Empty Row is not there
      setAddedRowId(-1); // Reset
      setAddedSplrNumber(null);
      // Disable Sorting to keep the new row on top
      apiRef.current.setSortModel([]);

      let obj = {
        id: nextRowId,
        number: defaultStatus,
        name: 'New supplier...',
        status: defaultStatus,
        r12Spend: 0,
        r12Forecast: 0,
        comments: '',
        unitGroupsSpend: []
      };

      setRows((oldRows) => [{ ...obj, isNew: true }, ...oldRows]);
      apiRef.current.setCellFocus(nextRowId, 'name');
    }
  }

  function processRowUpdate(
    updatedRow,
    originalRow,
    updateSupplierStatus,
    updateSupplierComments
  ) {
    let processStateChange = true;

    if (
      updatedRow.status === SUPPLIER_STATUS_REDUCE ||
      updatedRow.status === SUPPLIER_STATUS_DONOTUSE ||
      updatedRow.status === SUPPLIER_STATUS_LIMITED_USE
    ) {
      setShowSupplierCommentsEditModal(true);
      processStateChange = false;
    }

    if (updatedRow.status === defaultStatus) {
      apiRef.current.startCellEditMode({ id: 0, field: 'status' });
    }
    // Find what has changed, Comments or Status
    if (updatedRow.comments !== originalRow.comments) {
      // Comments changed
      if (processStateChange)
        updateSupplierComments(updatedRow.number, updatedRow.comments);
      // No refresh required
      setToggleRefresh(false);

      if (addedRowId !== -1) {
        // Reset it
        setAddedRowId(-1);
        // Refresh required
        setToggleRefresh(true);
      }
    } else if (updatedRow.status !== originalRow.status) {
      if (addedRowId !== -1 && updatedRow.number === addedSplrNumber) {
        // Add Row in-progress
        apiRef.current.startCellEditMode({ id: addedRowId, field: 'comments' });
        setToggleRefresh(false);
      }
      // Status Changed
      setOriginalSupplier(originalRow);
      setUpdatedSupplier(updatedRow);
      if (processStateChange)
        processRowUpdateForStatusOnly(updatedRow, updateSupplierStatus);
    }

    return updatedRow;
  }

  useEffect(() => {
    if (resetStatus)
      apiRef.current.startCellEditMode({ id: addedRowId, field: 'status' });
  }, [resetStatus]);

  const NoRowsMsg = () => (
    <Box>
      <Alert severity={'informational'}>
        No Strategic Suppliers have been added to this strategy
      </Alert>
    </Box>
  );

  return (
    <>
      <div>
        {isEdit && (
          <Box>
            <Button variant={'secondary'} onClick={addSupplier}>
              + Add Supplier
            </Button>
            {!isCreate && (
              <Button
                sx={{ float: 'right' }}
                startIcon={<fuel-icon name="delete" />}
                onClick={() => setShowClearExceptionsModal(true)}
                variant={'tertiary'}
              >
                Remove All Exceptions
              </Button>
            )}
          </Box>
        )}
        <Box sx={{ mt: isEdit ? 4 : 0 }}>
          <DataGridPro
            key={`SupplierGrid${refresh}`}
            autoHeight={true}
            apiRef={apiRef}
            density="compact"
            initialState={{
              sorting: {
                sortModel: [
                  addedSplrNumber ? {} : { field: 'r12Forecast', sort: 'desc' }
                ]
              },
              pinnedColumns: { right: ['actions'] }
            }}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            processRowUpdate={(updatedRow, originalRow) => {
              return processRowUpdate(
                updatedRow,
                originalRow,
                updateSupplierStatus,
                updateSupplierComments
              );
            }}
            slots={{
              detailPanelExpandIcon: () => (
                <fuel-icon name="keyboard_arrow_right" />
              ),
              detailPanelCollapseIcon: () => (
                <fuel-icon name="keyboard_arrow_down" />
              ),
              noRowsOverlay: NoRowsMsg
            }}
            columns={columns}
            rows={rows}
            getRowHeight={() => 'auto'}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            detailPanelExpandedRowIds={
              unitGroups?.length > 0 ? detailPanelExpandedRowIds : undefined
            }
            onDetailPanelExpandedRowIdsChange={
              handleDetailPanelExpandedRowIdsChange
            }
            onCellClick={handleCellClick}
            sx={supplierDataGridSx}
          />
        </Box>
      </div>
      {isEdit &&
        originalSupplier &&
        updatedSupplier &&
        showSupplierCommentsEditModal && (
          <SupplierStatusPopup
            showModal={showSupplierCommentsEditModal}
            handleCancel={handleCommentsModalCancel}
            supplier={originalSupplier}
            updatedSupplier={updatedSupplier}
            setShowModal={setShowSupplierCommentsEditModal}
            updateSupplierStatus={updateSupplierStatus}
            setResetStatus={setResetStatus}
            dispatchNonStrategicSupplier={dispatchNonStrategicSupplier}
            dispatchSupplier={dispatchSupplier}
            setNonStrategicSuppliers={setNonStrategicSuppliers}
            setNonSupplier={setNonSupplier}
            nonSupplier={nonSupplier}
            movingToStrategicOrNonStrategic={NON_STRATEGIC}
          />
        )}
      {isEdit && (
        <SMModal
          showModal={showClearExceptionsModal}
          handleCancel={handleModalClearExceptionsCancel}
          handleYes={removeAllExceptions}
          title="Remove All Exceptions?"
          msg_line1="Are you sure you want to remove all supplier status exceptions from the strategy?"
          cancelText="Cancel"
          yesText="Remove All Exceptions"
        />
      )}
    </>
  );
}
